import type { AccountOption } from "@app/components/account-combobox";
import type { StakeholderOption } from "@app/components/stakeholder-combobox";
import TxnTablePanel, { useTxnSort } from "@app/components/txn-table-panel";
import { useTxnPanelRefresh } from "@app/components/txn-table-panel/use-txn-panel-refresh";
import { useTxnBulkActions } from "@app/components/txn-table-panel/use-txn-bulk-actions";
import { getPerspectiveId } from "@app/lib/perspective";
import { Link } from "@tanstack/react-router";
import { Suspense, useState } from "react";
import { graphql, useLazyLoadQuery, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { booksLineDetailPage_Query } from "@app/queries/booksLineDetailPage_Query.graphql";
import type { booksLineDetailPageTxns_Query } from "@app/queries/booksLineDetailPageTxns_Query.graphql";

export const booksLineDetailQuery = graphql`
  query booksLineDetailPage_Query($code: String!) {
    booksLineDetail(code: $code) {
      code
      accountName
      totalCount
      limit
    }
  }
`;

// The editable transactions shell pulls this books-line's txns straight off the
// unified `transactions` resolver (booksLineCode drill), independent of the chrome
// query above, plus the option lists the shared bulk-action registry needs.
const txnsQuery = graphql`
  query booksLineDetailPageTxns_Query(
    $booksLineCode: String
    $perspectiveId: ID
    $sort: TxnSortEnum
    $sortDirection: String
  ) {
    transactions(
      booksLineCode: $booksLineCode
      perspectiveId: $perspectiveId
      sort: $sort
      sortDirection: $sortDirection
    ) {
      totalCount
      totalAmount
      limit
      txns {
        txn {
          id
        }
        md5
        postingDate
        description
        amount
        kind
        accountNumber
        internal
        classification
        counterparty {
          name
        }
        category
        expenseNature {
          id
          name
        }
      }
    }
    allStakeholders {
      id
      name
    }
    bankAccounts {
      id
      name
      nickname
      last4
    }
    expenseNatures(perspectiveId: $perspectiveId) {
      id
      name
    }
  }
`;

// The plumbing nodes (Root + per-account BASE) are never valid counterparties.
const isRealStakeholder = (s: { name: string }) =>
  !s.name.startsWith("&") &&
  !s.name.toLowerCase().includes("root stakeholder") &&
  s.name !== "_root";

export default function BooksLineDetailPage({
  queryRef,
}: {
  queryRef: PreloadedQuery<booksLineDetailPage_Query>;
}) {
  const { booksLineDetail: detail } = usePreloadedQuery(booksLineDetailQuery, queryRef);

  return (
    <div className="p-6 max-w-full mx-auto space-y-4">
      <Link to="/books" className="text-sm text-blue-600 hover:underline">
        ← all books
      </Link>

      <div>
        <h1 className="text-xl font-bold">{detail.accountName ?? detail.code}</h1>
        <p className="text-sm text-gray-500">
          {detail.code} · {detail.totalCount} transaction
          {detail.totalCount === 1 ? "" : "s"}
          {detail.totalCount > detail.limit ? ` (showing ${detail.limit})` : ""}
        </p>
      </div>

      <div className="bg-white rounded border overflow-hidden">
        <Suspense
          fallback={
            <p className="p-6 text-sm text-gray-400">Loading transactions…</p>
          }
        >
          <BooksLineTxns code={detail.code} />
        </Suspense>
      </div>
    </div>
  );
}

// The editable transactions list for this books line, fed by its own
// `transactions(booksLineCode: …)` query and wired to the shared bulk-action registry.
function BooksLineTxns({ code }: { code: string }) {
  const { refreshing, refresh, queryOptions } = useTxnPanelRefresh();
  const { sort, direction, onSort, sorting, sortVars } = useTxnSort();

  const data = useLazyLoadQuery<booksLineDetailPageTxns_Query>(
    txnsQuery,
    {
      booksLineCode: code,
      perspectiveId: getPerspectiveId() ?? undefined,
      ...sortVars,
    },
    queryOptions
  );
  const rows = data.transactions.txns;
  const stakeholderOptions: StakeholderOption[] =
    data.allStakeholders.filter(isRealStakeholder);
  const accountOptions: AccountOption[] = data.bankAccounts.map((a) => ({
    id: a.id,
    name: a.name,
    nickname: a.nickname,
    last4: a.last4,
  }));

  const { busy, renderBulkActions, dialogs } = useTxnBulkActions({
    enabled: ["setCounterparty", "createRule", "markInternal", "reclassify", "tagNature"],
    perspectiveId: getPerspectiveId(),
    onChanged: () => refresh(),
    rows,
    stakeholderOptions,
    accountOptions,
    natureOptions: data.expenseNatures,
  });

  const exportParams: Record<string, string> = {
    books_line_code: code,
    sort: sortVars.sort,
    sort_direction: sortVars.sortDirection,
  };
  const perspectiveId = getPerspectiveId();
  if (perspectiveId) exportParams.perspective_id = perspectiveId;

  return (
    <>
      <TxnTablePanel
        mode="inline"
        interaction="editable"
        title="Transactions"
        totalCount={data.transactions.totalCount}
        headerTotalDollars={data.transactions.totalAmount}
        rows={rows}
        emptyLabel="No transactions for this account."
        sort={sort}
        direction={direction}
        onSort={onSort} sorting={sorting}
        exportParams={exportParams}
        busy={busy || refreshing}
        renderBulkActions={renderBulkActions}
      />
      {dialogs}
    </>
  );
}
